import { schnorr } from '@noble/curves/secp256k1.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import { bech32 } from '@scure/base';

const STORAGE_KEY = 'nostr-privkey';

export interface NostrEvent {
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
}

export interface SignedNostrEvent extends NostrEvent {
  id: string;
  pubkey: string;
  sig: string;
}

function toHex(bytes: Uint8Array): string {
  return bytesToHex(bytes);
}

/** Generate a new secp256k1 keypair and store the private key in localStorage. */
export function generateKeypair(): { privateKey: string; publicKey: string } {
  const privKeyBytes = crypto.getRandomValues(new Uint8Array(32));
  const pubKeyBytes = schnorr.getPublicKey(privKeyBytes);
  const privKeyHex = toHex(privKeyBytes);
  const pubKeyHex = toHex(pubKeyBytes);
  localStorage.setItem(STORAGE_KEY, privKeyHex);
  return { privateKey: privKeyHex, publicKey: pubKeyHex };
}

/** Retrieve the stored keypair from localStorage. Returns null if not found. */
export function getStoredKeypair(): { privateKey: string; publicKey: string } | null {
  const privKeyHex = localStorage.getItem(STORAGE_KEY);
  if (!privKeyHex) return null;
  try {
    const privKeyBytes = hexToBytes(privKeyHex);
    const pubKeyBytes = schnorr.getPublicKey(privKeyBytes);
    return { privateKey: privKeyHex, publicKey: toHex(pubKeyBytes) };
  } catch {
    return null;
  }
}

/** Check if a private key is stored in localStorage. */
export function hasStoredKey(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Compute the Nostr event ID (sha256 of the serialized event).
 * Serialization: JSON.stringify([0, pubkey, created_at, kind, tags, content])
 */
function computeEventId(event: NostrEvent, pubkey: string): string {
  const serialized = JSON.stringify([
    0,
    pubkey,
    event.created_at,
    event.kind,
    event.tags,
    event.content,
  ]);
  const hash = sha256(new TextEncoder().encode(serialized));
  return toHex(hash);
}

export function decodeNsec(nsec: string): string {
  const { prefix, words } = bech32.decode(nsec);
  if (prefix !== 'nsec') throw new Error('Not a valid nsec');
  const bytes = bech32.fromWords(words);
  if (bytes.length !== 32) throw new Error('Invalid key length');
  return bytesToHex(bytes);
}

export function encodeNsec(hexPrivkey: string): string {
  const bytes = hexToBytes(hexPrivkey);
  return bech32.encode('nsec', bech32.toWords(bytes));
}

/** Sign a Nostr event with a given private key (hex). */
export function signEventWithKey(event: NostrEvent, privateKey: string): SignedNostrEvent {
  const privKeyBytes = hexToBytes(privateKey);
  const pubKeyBytes = schnorr.getPublicKey(privKeyBytes);
  const pubkey = toHex(pubKeyBytes);
  const id = computeEventId(event, pubkey);
  const sigBytes = schnorr.sign(hexToBytes(id), privKeyBytes);
  return {
    ...event,
    id,
    pubkey,
    sig: toHex(sigBytes),
  };
}
